import isNil from 'lodash-es/isNil';
import isNumber from 'lodash-es/isNumber';
import moment from 'moment-timezone';
import { Resource } from '../Resource';
import * as methods from '../methods';
const rootPath = 'api/oad/decitrait';
/** We do not handle botrytis (3) */
export var DecitraitDiseaseId;
(function (DecitraitDiseaseId) {
    DecitraitDiseaseId["mildew"] = "1";
    DecitraitDiseaseId["oidium"] = "2";
})(DecitraitDiseaseId || (DecitraitDiseaseId = {}));
export const decitraitDateFormat = 'DD/MM/YYYY';
/**
 * Stages marked as "possible in reality" by Decitrait
 */
export const displayableStagesId = [
    1, 3, 5, 6, 7, 9, 10, 12, 14, 15, 17, 19, 23, 25, 27, 29, 31, 32, 33, 34, 35, 36, 37, 43, 47
];
const stringifyId = (x) => ({ ...x, id: x.id.toString() });
const numberifyId = (x) => ({ ...x, id: parseInt(x.id, 10) });
const fromRaw = {
    optidose: (x) => ({
        copperDose: x['dose-cuivre'],
        doses: x.doses.map((y) => ({ diseaseId: y.maladie, productId: y.produit, quantity: y.dose }))
    }),
    createSanitaryStateNote: () => undefined,
    deleteTreatment: () => undefined,
    createTreatment: () => undefined,
    treatmentsPerYear: (x) => x.map(fromRaw.treatmentDetail),
    treatmentDetail: (x) => ({
        id: x.id,
        treatmentDate: moment(x.date, decitraitDateFormat).toDate(),
        foliageHeight: x['hauteur-feuillage'],
        foliageWidth: x['largeur-feuillage'],
        mildewPressure: x['pression-mildiou'],
        oidiumPressure: x['pression-oidium'],
        plots: x.parcelles,
        products: x.produits.map((y) => ({
            diseaseId: y.maladie,
            productId: y.produit.toString(),
            quantity: y.dose,
            batchNumber: y['no-lot']
        }))
    }),
    products: (x) => Object.entries(x).reduce((acc, [k, products]) => {
        const diseaseId = k;
        if (typeof acc[diseaseId] === 'undefined') {
            acc[diseaseId] = [];
        }
        acc[diseaseId] = products.map((x) => ({
            id: x.id,
            name: x.name,
            approvedDose: x['dose-homologuee'],
            infos: x.infos,
            categories: x.categories,
            substances: x.substances
        }));
        return acc;
    }, {}),
    treatmentCategories: (x) => x.map((y) => ({
        id: y.id,
        maxNumber: parseInt(y['nb-max'], 10),
        codes: y.codes
    })),
    plotDetail: (x) => ({
        plotArea: parseFloat(x.superficie),
        name: x.nom,
        latitude: parseFloat(x.latitude),
        longitude: parseFloat(x.longitude),
        interPlant: parseFloat(x.intercep),
        interRow: parseFloat(x.interrang),
        harvestDate: x['date-recolte'],
        trainingMethod: x.conduite,
        compactness: x.compacite,
        mildewSensibility: x['sensibilite-mildiou'],
        oidiumSensibility: x['sensibilite-oidium'],
        hadOidiumAttack: x['attaque-oidium'],
        grapeVariety: x.cepage
    }),
    connect: (x) => ({
        epicureUsername: x.login,
        organizationId: x.organization,
        password: x.password
    }),
    createUser: (x) => ({
        email: x.email,
        epicureUsername: x.login,
        organizationId: x.organization,
        firstName: x.first_name,
        lastName: x.last_name,
        password: x.password
    }),
    getUser: (x) => ({
        postalCode: x.cp,
        areaId: x.bassin,
        vineyardCastle: x.chateau,
        departmentId: x.departement,
        town: x.commune,
        responsable: x.responsable
    }),
    configureUser: (x) => fromRaw.getUser(x),
    getResources: ({ bassins, cepages, compacites, effeuillages, enherbements, fertilisations, historiquesparcellaires, ndvis, ouinon, portegreffes, prbs, tauxcontacts, vigueurs, sensibilites, conduites, maladies, stadespheno }) => ({
        area: bassins,
        grapeVariety: cepages,
        compactness: compacites,
        leafStripping: effeuillages,
        cropCover: enherbements,
        fertilisation: fertilisations,
        plotHistory: historiquesparcellaires,
        normalizedDifferenceVegetationIndex: ndvis,
        yesNo: ouinon,
        rootstock: portegreffes,
        grapeBerryReceptivityPotential: prbs,
        contactRate: tauxcontacts,
        vigour: vigueurs,
        sensibility: sensibilites.map(stringifyId),
        trainingMethod: conduites.map(stringifyId),
        disease: maladies.map(stringifyId),
        stages: stadespheno.map(numberifyId)
    }),
    departmentsByArea: (x) => x,
    summary: (x) => x.data
        .map((x) => ({
        date: moment(x.date, decitraitDateFormat),
        blackrotProtection: x['protection-blackrot'],
        computedStage: parseInt(x['stade-pheno-calcule'], 10),
        userSetStage: x['stade-pheno-reel'] === null ? null : parseInt(x['stade-pheno-reel'], 10),
        mildew: {
            protected: x['protection-mildiou'],
            protectionNeeded: x['besoin-protection-mildiou'],
            risk: !isNil(x['risque-mildiou'])
                ? isNumber(x['risque-mildiou'])
                    ? x['risque-mildiou'].toString()
                    : x['risque-mildiou']
                : null,
            fta: !isNil(x['fta-mildiou'])
                ? isNumber(x['fta-mildiou'])
                    ? x['fta-mildiou'].toString()
                    : x['fta-mildiou']
                : null,
            notes: {
                leaves: x['es-mildiou-feuillage'],
                grapes: x['es-mildiou-grappes']
            }
        },
        oidium: {
            protected: x['protection-oidium'],
            protectionNeeded: x['besoin-protection-oidium'],
            risk: !isNil(x['risque-oidium'])
                ? isNumber(x['risque-oidium'])
                    ? x['risque-oidium'].toString()
                    : x['risque-oidium']
                : null,
            fta: !isNil(x['fta-oidium'])
                ? isNumber(x['fta-oidium'])
                    ? x['fta-oidium'].toString()
                    : x['fta-oidium']
                : null,
            notes: {
                leaves: x['es-oidium-feuillage'],
                grapes: x['es-oidium-grappes']
            }
        }
    }))
        .sort((a, b) => a.date.unix() - b.date.unix()),
    updatePlot: () => undefined,
    plots: (x) => x,
    createPlot: (x) => ({
        plotId: x.plot,
        name: x.nom,
        plotArea: x.superficie,
        harvestDate: x.date_recolte,
        trainingMethod: x.conduite,
        interPlant: x.intercep,
        interRow: x.interrang,
        mildewSensibility: x.sensibilite_mildiou,
        oidiumSensibility: x.sensibilite_oidium,
        hadOidiumAttack: x.attaque_oidium
    }),
    linkPlot: (x) => ({
        plotId: x.plot
    }),
    setStage: () => undefined
};
const toRaw = {
    optidose: (x) => ({
        hauteur_feuillage: x.foliageHeight,
        largeur_feuillage: x.foliageWidth,
        pression_mildiou: x.mildewPressure,
        pression_oidium: x.oidiumPressure,
        date: moment(x.treatmentDate).format(decitraitDateFormat),
        produits: x.products.map((y) => ({
            maladie: y.diseaseId,
            produit: y.productId
        }))
    }),
    createSanitaryStateNote: (x) => ({
        plot: x.plotId,
        date: x.date,
        es_mildiou_feuillage: x.mildew.leaves,
        es_mildiou_grappes: x.mildew.grapes,
        es_oidium_feuillage: x.oidium.leaves,
        es_oidium_grappes: x.oidium.leaves
    }),
    createTreatment: (x) => ({
        plot: x.plotId,
        hauteur_feuillage: x.foliageHeight,
        largeur_feuillage: x.foliageWidth,
        pression_mildiou: x.mildewPressure,
        pression_oidium: x.oidiumPressure,
        date: moment(x.treatmentDate).format(decitraitDateFormat),
        produits: x.products.map((y) => ({
            maladie: y.diseaseId,
            produit: y.productId,
            'no-lot': y.batchNumber,
            dose: y.quantity
        }))
    }),
    connect: (x) => ({
        login: x.epicureUsername,
        password: x.password,
        organization: x.organizationId
    }),
    createUser: (x) => ({
        email: x.email,
        login: x.epicureUsername,
        organization: x.organizationId,
        first_name: x.firstName,
        last_name: x.lastName,
        password: x.password
    }),
    configureUser: (x) => ({
        cp: x.postalCode,
        bassin: x.areaId,
        chateau: x.vineyardCastle,
        departement: x.departmentId,
        commune: x.town,
        responsable: x.responsable,
        organization: x.organizationId
    }),
    createPlot: (x) => ({
        plot: x.plotId,
        nom: x.name,
        superficie: x.plotArea,
        date_recolte: x.harvestDate,
        conduite: x.trainingMethod,
        intercep: x.interPlant,
        interrang: x.interRow,
        sensibilite_mildiou: x.mildewSensibility,
        sensibilite_oidium: x.oidiumSensibility,
        attaque_oidium: x.hadOidiumAttack
    }),
    updatePlot: (x) => ({
        nom: x.name,
        superficie: x.plotArea,
        date_recolte: x.harvestDate,
        conduite: x.trainingMethod,
        intercep: x.interPlant,
        interrang: x.interRow,
        sensibilite_mildiou: x.mildewSensibility,
        sensibilite_oidium: x.oidiumSensibility,
        attaque_oidium: x.hadOidiumAttack
    }),
    linkPlot: (x) => ({
        organization: x.orgId,
        plot: x.plotId
    }),
    setStage: ({ plotId, stage, date }) => ({
        plot: plotId,
        date: date.format(decitraitDateFormat),
        stage: `${stage}`
    })
};
const decitrait = Resource((defaultRequesterBuilder, { getCtx }) => {
    const host = getCtx().hosts.dss;
    /** So that we do not forget to use the correct host & rootPath */
    const requesterBuilder = (requesterBuilderArgs) => defaultRequesterBuilder({
        ...requesterBuilderArgs,
        path: `${rootPath}/${requesterBuilderArgs.path}`,
        requestCtx: { host, ...requesterBuilderArgs.requestCtx }
    });
    const SUMMARY_PATH = `resume`;
    return {
        connect: methods.create({
            rootPath: `connect`,
            fromRaw: fromRaw.connect,
            requesterBuilder,
            toRaw: toRaw.connect
        }),
        createUser: methods.create({
            rootPath: `create_user`,
            fromRaw: fromRaw.createUser,
            requesterBuilder,
            toRaw: toRaw.createUser
        }),
        getResources: ({ organizationId }) => methods.get({
            rootPath: `resources`,
            fromRaw: fromRaw.getResources,
            requesterBuilder,
            cacheConfig: {
                staleTime: 30 * 60 * 1000
            },
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        getUser: ({ organizationId }) => methods.get({
            rootPath: `get_account`,
            fromRaw: fromRaw.getUser,
            requesterBuilder,
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        configureUser: methods.create({
            rootPath: `update_account`,
            toRaw: toRaw.configureUser,
            fromRaw: fromRaw.configureUser,
            requesterBuilder
        }),
        departmentsByArea: ({ organizationId, areaId }) => methods.get({
            requesterBuilder,
            rootPath: `departements/${areaId}`,
            fromRaw: fromRaw.departmentsByArea,
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        summary: ({ plotId }) => methods.get({ requesterBuilder, rootPath: SUMMARY_PATH, fromRaw: fromRaw.summary })(plotId),
        /** Decitrait created plots */
        plots: ({ organizationId }) => methods.get({
            requesterBuilder,
            rootPath: `plot/list`,
            fromRaw: fromRaw.plots,
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        createPlot: methods.create({
            rootPath: `plot/create`,
            toRaw: toRaw.createPlot,
            fromRaw: fromRaw.createPlot,
            requesterBuilder
        }),
        updatePlot: ({ plotId }) => methods.create({
            rootPath: `plot/update/${plotId}`,
            toRaw: toRaw.updatePlot,
            fromRaw: fromRaw.updatePlot,
            requesterBuilder
        })(),
        plotDetail: ({ plotId }) => methods.get({
            rootPath: `plot/detail/${plotId}`,
            fromRaw: fromRaw.plotDetail,
            requesterBuilder
        })(),
        linkPlot: ({ decitraitPlotId }) => methods.create({
            rootPath: `plot/map/${decitraitPlotId}`,
            toRaw: toRaw.linkPlot,
            fromRaw: fromRaw.linkPlot,
            requesterBuilder
        })(),
        products: ({ organizationId }) => methods.get({
            requesterBuilder,
            rootPath: `traitements/produits`,
            fromRaw: fromRaw.products,
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        treatmentCategories: ({ organizationId }) => methods.get({
            requesterBuilder,
            rootPath: `traitements/categories`,
            fromRaw: fromRaw.treatmentCategories,
            params: {
                query: {
                    organization: organizationId
                }
            }
        })(),
        treatmentDetail: ({ treatmentId }) => methods.get({
            requesterBuilder,
            rootPath: `traitements/detail/${treatmentId}`,
            fromRaw: fromRaw.treatmentDetail
        })(),
        treatmentsPerYear: ({ plotId, organizationId, year }) => methods.get({
            requesterBuilder,
            rootPath: `traitements/list_per_year/${plotId}`,
            fromRaw: fromRaw.treatmentsPerYear,
            params: {
                query: {
                    year,
                    organization: organizationId
                }
            }
        })(),
        createTreatment: methods.create({
            rootPath: `traitements/create`,
            toRaw: toRaw.createTreatment,
            fromRaw: fromRaw.createTreatment,
            requesterBuilder
        }),
        deleteTreatment: ({ orgId, treatmentId }) => methods.deletion({
            rootPath: 'traitements/delete',
            requesterBuilder,
            params: { query: { organization: orgId } }
        })(treatmentId),
        createSanitaryStateNote: methods.create({
            requesterBuilder,
            rootPath: 'sanitary_state',
            fromRaw: fromRaw.createSanitaryStateNote,
            toRaw: toRaw.createSanitaryStateNote
        }),
        optidose: (plotId) => methods.create({
            rootPath: `optidose/${plotId}`,
            requesterBuilder,
            fromRaw: fromRaw.optidose,
            toRaw: toRaw.optidose
        })(),
        setStage: methods.create({
            requesterBuilder,
            rootPath: 'stadespheno/create',
            fromRaw: fromRaw.setStage,
            toRaw: toRaw.setStage,
            patternToInvalidate: SUMMARY_PATH
        })
    };
});
export default decitrait;
